import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  fetchIntraDocSearchHits,
  type IntraDocSearchHit,
} from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import type { FragmentHighlight } from 'features/knowledgeDocumentViewer/types'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import {
  extractContiguousMarkedPhrases,
  quoteSearchAttempts,
} from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { useEffect, useRef, useState } from 'react'

const MAX_PHRASE_ATTEMPTS = 3

export type FragmentHighlightState = {
  isActive: boolean
  isLoading: boolean
  hit: IntraDocSearchHit | null
}

export type PageSearch = (phrase: string) => Promise<IntraDocSearchHit | null>

async function findFirstPageHit(
  searchPage: PageSearch,
  phrases: string[]
): Promise<IntraDocSearchHit | null> {
  for (const phrase of phrases.slice(0, MAX_PHRASE_ATTEMPTS)) {
    const pageHit = await searchPage(phrase)
    if (pageHit) return pageHit
  }
  return null
}

async function findQuoteBoxes(
  searchPage: PageSearch,
  quote: string
): Promise<NormalizedBBox[]> {
  for (const phrases of quoteSearchAttempts(quote)) {
    const hits = await Promise.all(phrases.map(searchPage))
    const boxes = hits.flatMap((hit) => hit?.bounding_boxes ?? [])
    if (boxes.length > 0) return boxes
  }
  return []
}

export async function findQuotesPageHit(
  searchPage: PageSearch,
  quotes: string[],
  pageNum: number
): Promise<IntraDocSearchHit | null> {
  const boxesPerQuote = await Promise.all(
    quotes.map((quote) => findQuoteBoxes(searchPage, quote))
  )
  const boxes = boxesPerQuote.flat()
  return boxes.length > 0
    ? {
        page_number: pageNum,
        matched_text: quotes.join(' '),
        bounding_boxes: boxes,
      }
    : null
}

export function useFragmentHighlight(
  documentId: string,
  siteId: string,
  fragmentHighlight: FragmentHighlight | undefined,
  { intraSearchIsOpen = false }: { intraSearchIsOpen?: boolean } = {}
): FragmentHighlightState {
  const [hit, setHit] = useState<IntraDocSearchHit | null>(null)
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { authorizedAxios: axios } = useGeckoAuth()
  const { isPreviewMode } = usePipelineConfig()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()

  const axiosRef = useRef(axios)
  axiosRef.current = axios
  const urlRef = useRef(POWER_BACKEND_AI_URL)
  urlRef.current = POWER_BACKEND_AI_URL

  const fragmentKey = fragmentHighlight
    ? `${documentId}:${siteId}:${fragmentHighlight.pageNum}:${fragmentHighlight.fragmentText}:${fragmentHighlight.quotes?.join('\n') ?? ''}`
    : null

  useEffect(() => {
    if (intraSearchIsOpen) {
      setHit(null)
      setIsActive(false)
      setIsLoading(false)
    }
  }, [intraSearchIsOpen])

  useEffect(() => {
    if (!fragmentHighlight || !fragmentKey) {
      setHit(null)
      setIsActive(false)
      setIsLoading(false)
      return
    }

    const quotes = fragmentHighlight.quotes ?? []
    // Fragment text may be marked-up snippet HTML (search results) or a plain
    // string (an anchor id / cited phrase from an evidence panel). Marked
    // phrases are the highest-precision needles; fall back to the raw text so
    // plain-string fragments highlight instead of silently no-oping.
    const marked = extractContiguousMarkedPhrases(
      fragmentHighlight.fragmentText
    )
    const plain = fragmentHighlight.fragmentText.trim()
    const phrases =
      marked.length > 0 ? marked : plain && !plain.includes('<') ? [plain] : []
    if (quotes.length === 0 && phrases.length === 0) return

    const controller = new AbortController()
    const { pageNum } = fragmentHighlight
    const searchPage: PageSearch = async (phrase) => {
      if (controller.signal.aborted) return null
      try {
        const hits = await fetchIntraDocSearchHits(
          axiosRef.current,
          urlRef.current,
          documentId,
          siteId,
          phrase,
          controller.signal,
          200,
          isPreviewMode
        )
        return hits.find((hit) => hit.page_number === pageNum) ?? null
      } catch {
        return null
      }
    }
    setHit(null)
    setIsActive(true)
    setIsLoading(true)

    const located =
      quotes.length > 0
        ? findQuotesPageHit(searchPage, quotes, pageNum)
        : findFirstPageHit(searchPage, phrases)

    located
      .then((result) => {
        if (controller.signal.aborted) return
        setHit(result)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setHit(null)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      })

    return () => controller.abort()
  }, [fragmentKey, documentId, siteId, fragmentHighlight, isPreviewMode])

  return { isActive, isLoading, hit }
}
